import React, { useState, useMemo, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import MapComponent from '../components/Map';
import SearchableSelect from '../components/SearchableSelect';
import useAppStore from '../store';
import { useNavigate } from 'react-router-dom';

// Coordonnées du dépôt de Bourg de Visa
const BOURG_DE_VISA_POSITION = { lat: 44.260571, lng: 0.960050 };

const MapPage: React.FC = () => {
  const navigate = useNavigate();
  const { drivers, tasks, dumpsters, depots, loadInitialData } = useAppStore();
  const [selectedDriver, setSelectedDriver] = useState<string | null>(null);
  const [selectedDumpster, setSelectedDumpster] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(BOURG_DE_VISA_POSITION);
  const [mapZoom, setMapZoom] = useState<number>(13);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Rafraîchissement automatique toutes les 4 minutes
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        await loadInitialData();
      } catch (error) {
        console.error('Error during auto-refresh:', error);
      }
    }, 4 * 60 * 1000); // 4 minutes

    return () => clearInterval(interval);
  }, [loadInitialData]);
  const handleDriverChange = (driverId: string) => {
    setSelectedDriver(driverId || null);
    setSelectedDumpster(null);

    if (driverId) {
      const driver = drivers.find(d => d.id === driverId);
      if (driver?.position) {
        setMapCenter(driver.position);
      }
    } else {
      setMapCenter(BOURG_DE_VISA_POSITION);
    }

  };

  const handleDumpsterChange = (dumpsterId: string) => {
    setSelectedDumpster(dumpsterId || null);
    setSelectedDriver(null);

    if (dumpsterId) {
      const dumpster = dumpsters.find(d => d.id === dumpsterId);
      if (dumpster?.position) {
        setMapCenter(dumpster.position);
        setMapZoom(16);
      }
    } else {
      setMapCenter(BOURG_DE_VISA_POSITION);
      setMapZoom(13);
    }

  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadInitialData();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Grouper les chauffeurs par statut
  const groupedDrivers = drivers.reduce((acc, driver) => {
    const hasActiveTasks = tasks.some(task =>
      task.driverId === driver.id && task.status === 'in-progress'
    );

    if (driver.isOnline && hasActiveTasks) {
      acc.active.push(driver);
    } else if (driver.isOnline) {
      acc.available.push(driver);
    } else {
      acc.inactive.push(driver);
    }

    return acc;
  }, {
    active: [] as typeof drivers,
    available: [] as typeof drivers,
    inactive: [] as typeof drivers
  });

  // Préparer les options des chauffeurs
  const driverOptions = useMemo(() => {
    const options = [];

    if (groupedDrivers.active.length > 0) {
      options.push({ value: '', label: '── En mission ──', disabled: true });
      options.push(...groupedDrivers.active
        .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'fr'))
        .map(driver => ({
          value: driver.id,
          label: `${driver.firstName} ${driver.lastName} 🟢 ${driver.power ? '⚡️' : ''}`
        })));
    }

    if (groupedDrivers.available.length > 0) {
      options.push({ value: '', label: '── Disponibles ──', disabled: true });
      options.push(...groupedDrivers.available
        .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'fr'))
        .map(driver => ({
          value: driver.id,
          label: `${driver.firstName} ${driver.lastName} 🟡 ${driver.power ? '⚡️'  : ''}`
        })));
    }

    if (groupedDrivers.inactive.length > 0) {
      options.push({ value: '', label: '── Déconnectés ──', disabled: true });
      options.push(...groupedDrivers.inactive
        .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'fr'))
        .map(driver => ({
          value: driver.id,
          label: `${driver.firstName} ${driver.lastName} ⚫ ${driver.power ? '⚡️' : ''}`
        })));
    }

    return options;
  }, [groupedDrivers]);

  // Filter dumpsters that are not in a depot
  const dumpsterOptions = useMemo(() => {
    return dumpsters
      .filter(dumpster => dumpster.position && !dumpster.depotId)
      .sort((a, b) => a.number.localeCompare(b.number))
      .map(dumpster => ({
        value: dumpster.id,
        label: `${dumpster.name} (${dumpster.number})`
      }));
  }, [dumpsters]);

  // Only show dumpsters that have a GPS position from a completed pose task and are not in a depot
  const dumpsterMarkers = useMemo(() => {
    return dumpsters
      .filter(d => d.position && !d.depotId)
      .map(d => ({
        ...d,
        selected: d.id === selectedDumpster
      }));
  }, [dumpsters, selectedDumpster]);
  
  return (
    <div className="h-[calc(100vh-8rem)]">
      <div className="bg-white rounded-lg shadow mb-4 p-4 flex flex-col sm:flex-row justify-between items-center gap-2">
        <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
          <SearchableSelect
            options={driverOptions}
            value={selectedDriver || ''}
            onChange={handleDriverChange}
            placeholder="Tous les chauffeurs"
            searchPlaceholder="Rechercher un chauffeur..."
            className="w-full sm:w-64"
          />

          <SearchableSelect
            options={dumpsterOptions}
            value={selectedDumpster || ''}
            onChange={handleDumpsterChange}
            placeholder="Toutes les bennes"
            searchPlaceholder="Rechercher par numéro..."
            className="w-full sm:w-64"
          />

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`p-2 rounded-lg border border-gray-300 hover:bg-gray-50 ${
              isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="Rafraîchir"
          >
            <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      
      <div className="h-full">
        <MapComponent
          center={mapCenter}
          zoom={mapZoom}
          dumpsters={dumpsterMarkers}
          showSaveGpsButton={false}
          navigate={navigate}
        />
      </div>
    </div>
  );
};

export default MapPage;